import React, {useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {
  Popper,
  Stack,
  MenuItem,
  ClickAwayListener,
  TextField,
} from '@mui/material';
import {designSystemToken, Icon, Body} from '@lightricks/react-design-system';
import {raiseContentStudioError} from '@/utils/contentStudioErrorHandling';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import {
  ASSET_FILTERS,
  ASSETS,
  CONTENT_LAB,
} from '@/hooks/content-studio/consts';
import useCreateFolder from '@/hooks/content-studio/mutations/use-create-folder';
import useMoveFolder from '@/hooks/content-studio/mutations/use-move-folder';
import useContentLabFoldersQuery from '@/hooks/content-studio/use-folders-query';
import useContentStudioStore, {
  contentStudioActions,
  ErrorKind,
} from '@/stores/contentStudioStore';

type FolderPopperProps = {
  anchorElement: HTMLElement | null;
  onClickAway: () => void;
};

function FolderPopper({anchorElement, onClickAway}: FolderPopperProps) {
  const {selectedAssets} = useContentStudioStore(
    useShallow((state) => ({
      selectedAssets: state.selectedAssets,
    }))
  );

  const {mutateAsync: moveAssetsToFolder} = useMoveFolder();
  const {mutateAsync: createFolder} = useCreateFolder();
  const [newFolderName, setNewFolderName] = useState('');
  const {data: folders} = useContentLabFoldersQuery();
  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Popper
        open={!!anchorElement}
        anchorEl={anchorElement}
        placement="top-start"
        modifiers={[
          {
            name: 'offset',
            options: {offset: [0, 30]},
          },
        ]}
        sx={{zIndex: 1000}}
      >
        <Stack
          sx={{
            backgroundColor: designSystemToken('semantic.bg.secondary'),
            borderRadius: '16px',
            boxShadow:
              '0px 5px 26px 4px rgba(0, 0, 0, 0.12), 0px 14px 21px 2px rgba(0, 0, 0, 0.14), 0px 7px 9px -4px rgba(0, 0, 0, 0.20)',
            padding: '8px',
            maxHeight: '300px',
            overflow: 'auto',
          }}
        >
          {folders?.map((folder) => (
            <MenuItem
              key={folder.id}
              sx={{gap: '12px'}}
              onClick={async () => {
                try {
                  await moveAssetsToFolder({
                    folderId: folder.id,
                    assetIds: selectedAssets.map((asset) => asset.id),
                  });
                  contentStudioActions.setSelectedAssets([]);
                  contentStudioActions.setProjectFilters(undefined);
                  onClickAway();
                } catch (error) {
                  raiseContentStudioError(
                    translate('Failed to move assets to folder'),
                    ErrorKind.FAILURE
                  );
                }
              }}
            >
              <Icon
                name="Navigation-Projects"
                size="medium"
                appearance="primary"
              />
              <Body size="lg">{folder.name}</Body>
            </MenuItem>
          ))}
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            gap="12px"
            padding="0px 16px 6px 16px"
            height="48px"
          >
            <Icon
              name="Navigation-Projects"
              size="medium"
              appearance="primary"
            />
            <TextField
              placeholder="Create new folder"
              value={newFolderName}
              onChange={(e) => {
                setNewFolderName(e.target.value);
              }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  try {
                    const newFolder = await createFolder({name: newFolderName});
                    setNewFolderName('');
                    await moveAssetsToFolder({
                      folderId: newFolder.id,
                      assetIds: selectedAssets.map((asset) => asset.id),
                    });
                    contentStudioActions.setSelectedAssets([]);
                    contentStudioActions.setProjectFilters(undefined);
                    onClickAway();
                  } catch (error) {
                    raiseContentStudioError(
                      translate('Failed to move assets to folder'),
                      ErrorKind.FAILURE
                    );
                  }
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  height: '40px',
                  padding: '0 14px',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '16px',
                },
              }}
            />
          </Stack>
        </Stack>
      </Popper>
    </ClickAwayListener>
  );
}

export default FolderPopper;
